import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button, Snackbar,  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ErrorDialog from "../ErrorDialog/ErrorDialog";
import {
  createFilterApi,
  deleteFilterApi,
  getFiltersApi,
  updateFilterApi,
} from "../../../api/filterApi";

import { FilterPayload, SavedFilter, UpdateFilterPayload } from "../../../types/filterTypes";
import ButtonComponent from "../button/Button";

type Props = {
  open: boolean;
  onClose: () => void;
  gridRef: any;
  tableName: string;
  userEmail: any;
  activeFilter?: any;
  externalFilterState?: Record<string, any> | null;
  onApplyExternalFilterState?: (
    state: Record<string, any> | null,
  ) => void;
  onFiltersChanged?: (
    filters: SavedFilter[],
  ) => void;
  onSavedFilterApplied?: () => void;
};

const FILTER_META_KEY = "__filterManagerMeta";

const splitSavedFilterModel = (model: any) => {
  if (!model || typeof model !== "object") {
    return {
      gridModel: {},
      externalState: null,
    };
  }

  const cloned = { ...model } as Record<string, any>;
  const meta = cloned[FILTER_META_KEY];
  delete cloned[FILTER_META_KEY];

  return {
    gridModel: cloned,
    externalState: meta?.externalState ?? null,
  };
};

const hasMeaningfulExternalFilterState = (
  state: Record<string, any> | null | undefined,
) => {
  if (!state || typeof state !== "object") return false;
  return Object.keys(state).length > 0;
};

const hasMeaningfulGridFilter = (
  model: Record<string, any> | null | undefined,
) => {
  if (!model || typeof model !== "object") return false;

  return Object.values(model).some((value) => {
    if (value == null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") {
      return Object.keys(value).length > 0;
    }
    return true;
  });
};

const FilterManagerModal: React.FC<Props> = ({
  open,
  onClose,
  gridRef,
  tableName,
  userEmail,
  activeFilter,
  externalFilterState,
  onApplyExternalFilterState,
  onFiltersChanged,
  onSavedFilterApplied,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filters, setFilters] = useState<SavedFilter[]>([]);

  const [selectedFilterId, setSelectedFilterId] =
    useState<number | "">("");

  const [filterName, setFilterName] = useState("");

  const [description, setDescription] = useState("");

  const [isDefault, setIsDefault] = useState(false);

  const [editingFilter, setEditingFilter] =
    useState<SavedFilter | null>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * Persists the ID of the last filter the user explicitly clicked "Apply" on.
   * Survives modal open/close so that when the user reopens after adding extra
   * table-level filters, the same filter is pre-selected and "Update" is
   * enabled even though the current grid state no longer matches it exactly.
   */
  const lastAppliedFilterIdRef = useRef<number | undefined>(undefined);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toastOpen, setToastOpen] = useState(false);

  const [toastMessage, setToastMessage] = useState("");

  const [toastSeverity, setToastSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");

  const loadFilters = async (
    preferredFilterId?: number,
  ) => {
    try {
      setLoading(true);

      const response = await getFiltersApi(
        userEmail,
        tableName,
      );

      const list = response || [];
      setFilters(list);
      onFiltersChanged?.(list);

      // Keep selection stable when possible, without forcing edit mode.
      const currentFilterModel =
        gridRef?.current?.api?.getFilterModel?.() || {};
      const hasGridFilter =
        Object.keys(currentFilterModel).length > 0;

      let nextSelectedId: number | "" = "";

      // 0. Detect a full reset FIRST — before any pre-selection logic runs.
      //    Saved filters are column-filter only, so reset detection is based
      //    solely on AG Grid filter model state.
      const isFullReset = !hasGridFilter;
      if (isFullReset) {
        lastAppliedFilterIdRef.current = undefined;
      }

      // 1. Honour an explicit caller-supplied preferred ID (e.g. after
      //    save/update) — but only when the grid has not been fully reset.
      if (!isFullReset && preferredFilterId) {
        const exists = list.find(
          (item) => item.id === preferredFilterId,
        );
        if (exists) {
          nextSelectedId = exists.id;
          lastAppliedFilterIdRef.current = exists.id;
        }
      }

      // 2. Fall back to the last filter the user applied (e.g. after adding
      //    extra column filters on top of an applied filter) — again skipped
      //    on full reset.
      if (!isFullReset && !nextSelectedId && lastAppliedFilterIdRef.current) {
        const exists = list.find(
          (item) => item.id === lastAppliedFilterIdRef.current,
        );
        if (exists) {
          nextSelectedId = exists.id;
        }
      }

      // 3. Try exact match against current grid filter state.
      if (
        !nextSelectedId &&
        hasGridFilter
      ) {
        const matchedFilter = list.find(
          (filter) => {
            const {
              gridModel,
            } = splitSavedFilterModel(
              filter.filters,
            );

            const isGridMatch =
              JSON.stringify(gridModel) ===
              JSON.stringify(currentFilterModel);

            return isGridMatch;
          },
        );

        if (matchedFilter) {
          nextSelectedId = matchedFilter.id;
          // Persist this match so selection remains stable even after
          // users tweak non-grid state (e.g., All/Inversion) before reopening.
          lastAppliedFilterIdRef.current = matchedFilter.id;
        }
      }

      setSelectedFilterId(nextSelectedId);
    } catch (error: any) {
      console.error(error);
      setToastSeverity("error");
      setToastMessage(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to load filters",
      );
      setToastOpen(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (open) {
      // Pass the last-applied ID so the dropdown stays on the same filter
      // even after the user added extra column filters on top of it.
      loadFilters(lastAppliedFilterIdRef.current);
    } else {
      resetForm();
      setSelectedFilterId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetForm = () => {
    setFilterName("");
    setDescription("");
    setIsDefault(false);
    setEditingFilter(null);
    setErrors({});
    setActiveTab(0);
  };

  const selectedFilter = useMemo(() => {
    return filters.find(
      (item) => item.id === selectedFilterId,
    );
  }, [filters, selectedFilterId]);

  const validate = () => {
    const validationErrors: Record<
      string,
      string
    > = {};

    const trimmedName = filterName.trim();

    if (!trimmedName) {
      validationErrors.name =
        "Filter name is required";
    }

    if (
      trimmedName &&
      trimmedName.length < 3
    ) {
      validationErrors.name =
        "Minimum 3 characters required";
    }

    if (trimmedName.length > 100) {
      validationErrors.name =
        "Maximum 100 characters allowed";
    }

    if (description.length > 500) {
      validationErrors.description =
        "Maximum 500 characters allowed";
    }

    const duplicate = filters.some(
      (filter) =>
        filter.name.trim().toLowerCase() ===
          trimmedName.toLowerCase() &&
        filter.id !== editingFilter?.id,
    );

    if (duplicate) {
      validationErrors.name =
        "Filter name already exists";
    }

    const filterModel =
      gridRef?.current?.api?.getFilterModel();

    if (!editingFilter) {
      const hasAnyGridFilter =
        !!gridRef?.current?.api?.isAnyFilterPresent?.();
      const hasGridFilter = hasAnyGridFilter
        ? true
        : hasMeaningfulGridFilter(filterModel);
      const quickFilterText =
        gridRef?.current?.api?.getQuickFilter?.() ||
        gridRef?.current?.api?.getGridOption?.("quickFilterText") ||
        "";
      const hasQuickFilter =
        typeof quickFilterText === "string" &&
        quickFilterText.trim().length > 0;

      const hasExternalFilter =
        hasMeaningfulExternalFilterState(externalFilterState);

      if (!hasGridFilter && !hasQuickFilter && !hasExternalFilter) {
      validationErrors.filter =
          "Please apply at least one filter before saving";
      }
    }

    setErrors(validationErrors);

    return (
      Object.keys(validationErrors).length === 0
    );
  };

  const handleSaveOrUpdate = async () => {
    try {
      if (!validate()) return;
      setSaving(true);

      const currentFilterModel =
        gridRef?.current?.api?.getFilterModel?.() ||
        {};

      const hasCurrentFilter =
        Object.keys(currentFilterModel).length > 0;

      const existingMeta = splitSavedFilterModel(
        editingFilter?.filters,
      );

      // When editing, only pull in the live grid state if the filter being
      // edited is the one that was last applied (so we capture any extra
      // column filters the user added on top).  For any other filter we
      // preserve its existing data so a rename/description change does not
      // accidentally overwrite it with a completely different filter state.
      const isEditingAppliedFilter =
        editingFilter != null &&
        lastAppliedFilterIdRef.current === editingFilter.id;

      const resolvedFilters = editingFilter
        ? isEditingAppliedFilter && hasCurrentFilter
          ? currentFilterModel
          : existingMeta.gridModel || {}
        : currentFilterModel;

      const resolvedExternalState = editingFilter
        ? isEditingAppliedFilter
          ? externalFilterState ?? null
          : existingMeta.externalState ?? null
        : externalFilterState ?? null;

      const persistedFilterPayload = {
        ...resolvedFilters,
        ...(hasMeaningfulExternalFilterState(resolvedExternalState)
          ? {
            [FILTER_META_KEY]: {
              externalState: resolvedExternalState,
            },
          }
          : {}),
      };

      const payload: FilterPayload = {
        createdBy: userEmail,
        tableName,
        name: filterName.trim(),
        description: description.trim(),
        default: isDefault,
        filters: persistedFilterPayload,
      };

      let savedFilterId: number | undefined;

      if (editingFilter) {
        const existingPersistedFilters = {
          ...(existingMeta.gridModel || {}),
          ...(hasMeaningfulExternalFilterState(existingMeta.externalState)
            ? {
              [FILTER_META_KEY]: {
                externalState: existingMeta.externalState,
              },
            }
            : {}),
        };
        const hasFilterChanged =
          JSON.stringify(persistedFilterPayload) !==
          JSON.stringify(existingPersistedFilters);

        const updatePayload: UpdateFilterPayload = {
          id: editingFilter?.id,
          createdBy: userEmail,
          default: isDefault,
          tableName,
          name: filterName.trim(),
          description: description.trim(),
        };

        if (hasFilterChanged) {
          updatePayload.filters =
            persistedFilterPayload;
        }

        const updated = await updateFilterApi(
          updatePayload,
        );
        savedFilterId = updated?.id;
      } else {
        const created = await createFilterApi(
          payload,
        );
        savedFilterId = created?.id;
      }

      await loadFilters(savedFilterId);

      resetForm();

      setActiveTab(0);

      setToastSeverity("success");

      setToastMessage(
        editingFilter
          ? "Filter updated successfully"
          : "Filter saved successfully",
      );
      setToastOpen(true);
    } catch (error: any) {
      console.log(error);
      setToastSeverity("error");

      setToastMessage(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to save filter",
      );

      setToastOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCurrentFilter = async () => {
    if (!selectedFilter || !gridRef?.current?.api) return;

    try {
      setSaving(true);

      const currentFilterModel =
        gridRef?.current?.api?.getFilterModel?.() || {};

      const existingMeta = splitSavedFilterModel(selectedFilter.filters);

      const hasCurrentFilter =
        Object.keys(currentFilterModel).length > 0;
      const resolvedFilters = hasCurrentFilter
        ? currentFilterModel
        : existingMeta.gridModel || {};

      const resolvedExternalState =
        externalFilterState ?? existingMeta.externalState ?? null;

      const hasAnyFilter =
        hasCurrentFilter ||
        hasMeaningfulExternalFilterState(resolvedExternalState);

      if (!hasAnyFilter) {
        setToastSeverity("warning");
        setToastMessage(
          "No active filter to save. Apply at least one filter first.",
        );
        setToastOpen(true);
        return;
      }

      const persistedFilterPayload = {
        ...resolvedFilters,
        ...(hasMeaningfulExternalFilterState(resolvedExternalState)
          ? {
            [FILTER_META_KEY]: {
              externalState: resolvedExternalState,
            },
          }
          : {}),
      };

      const updatePayload: UpdateFilterPayload = {
        id: selectedFilter.id,
        createdBy: userEmail,
        default: !!selectedFilter.default,
        tableName,
        name: selectedFilter.name,
        description: selectedFilter.description || "",
        filters: persistedFilterPayload,
      };

      await updateFilterApi(updatePayload);
      await loadFilters(selectedFilter.id);

      setToastSeverity("success");
      setToastMessage(
        `"${selectedFilter.name}" updated with current filter`,
      );
      setToastOpen(true);
    } catch (error: any) {
      setToastSeverity("error");
      setToastMessage(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to update filter",
      );
      setToastOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleApply = () => {
    if (!selectedFilter || !gridRef?.current?.api) return;

    const { gridModel, externalState } =
      splitSavedFilterModel(selectedFilter.filters);

    gridRef.current.api.setFilterModel(
      gridModel,
    );

    gridRef.current.api.onFilterChanged();
    onApplyExternalFilterState?.(externalState);
    // Remember which filter was applied so the dropdown stays selected
    // when the user reopens the modal after modifying the grid further.
    lastAppliedFilterIdRef.current = selectedFilter.id;
    onSavedFilterApplied?.();
setToastSeverity("success");
setToastMessage(`${selectedFilter.name} applied successfully`);
setToastOpen(true);
    onClose();
  };

  const handleStartEdit = () => {
    if (!selectedFilter) return;

    setEditingFilter(selectedFilter);
    setFilterName(selectedFilter.name || "");
    setDescription(
      selectedFilter.description || "",
    );
    setIsDefault(!!selectedFilter.default);
    setErrors({});
    setActiveTab(1);
  };

  const handleStartCreate = () => {
    resetForm();
    setActiveTab(1);
  };

  const handleDelete = async () => {
    if (!selectedFilterId) return;

    try {
      await deleteFilterApi(
        Number(selectedFilterId),
      );

      const deletingFilter = filters.find(
        (item) => item.id === selectedFilterId,
      );
      const currentFilterModel =
        gridRef?.current?.api?.getFilterModel?.();
      const deletingGridModel = splitSavedFilterModel(
        deletingFilter?.filters,
      ).gridModel;
      const isDeletingAppliedFilter =
        JSON.stringify(
          deletingGridModel || {},
        ) === JSON.stringify(currentFilterModel || {});

      if (
        isDeletingAppliedFilter &&
        gridRef?.current?.api
      ) {
        gridRef.current.api.setFilterModel(null);

        if (
          typeof gridRef.current.api.setGridOption ===
          "function"
        ) {
          gridRef.current.api.setGridOption(
            "quickFilterText",
            "",
          );
        }

        gridRef.current.api.onFilterChanged();
      }

      await loadFilters();
      resetForm();
      setDeleteDialogOpen(false);

      setToastSeverity("success");
      setToastMessage(
        "Filter deleted successfully",
      );
      setToastOpen(true);
    } catch (error: any) {
      setToastSeverity("error");
      setToastMessage(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to delete filter",
      );
      setToastOpen(true);
    }
  };
  const handleFilterSelection = (filterId: number) => {
    const filter = filters.find(
      (item) => item.id === filterId,
    );

    if (!filter) return;

    setSelectedFilterId(filter.id);
    setEditingFilter(null);
    setErrors({});
  };



  return (
    <><Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Saved Filters
      </DialogTitle>

      <DialogContent>
        <Tabs
         
        textColor="inherit"
        TabIndicatorProps={{ style: { backgroundColor: "#000", height: 2 } }}
          value={activeTab}
          onChange={(_, value) => {
            if (value === 0) {
              setActiveTab(0);
              return;
            }

            // Opening Create/Edit tab from header defaults to create mode.
            handleStartCreate();
          }}
           sx={{
          mb: 2,
          borderBottom: "1px solid #d9d9d9",
          "& .MuiTab-root": {
            color: "#333",
            textTransform: "none",
            fontWeight: 500,
            minHeight: 44,
          },
          "& .MuiTab-root.Mui-selected": {
            color: "#000",
            fontWeight: 600,
          },
          "& .MuiTab-root.Mui-focusVisible": {
            outline: "1px solid #000",
            outlineOffset: -1,
          }
         }}
        >
          <Tab label="Select Filter" />

          <Tab label="Create / Edit Filter" />
        </Tabs>

        {activeTab === 0 && (
          <Stack spacing={3}>
            <Select
              fullWidth
              displayEmpty
              disabled={loading}
              sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
              value={selectedFilterId?selectedFilterId:""}
              onChange={(e) =>
                handleFilterSelection(
                  Number(e.target.value),
                )
              }
            >
              <MenuItem value="">
                Select a Filter
              </MenuItem>

              {filters.map((filter) => (
                <MenuItem
                  key={filter.id}
                  value={filter.id}
                >
                  {filter.name}
                  {filter.default
                    ? " (Default)"
                    : ""}
                </MenuItem>
              ))}
            </Select>

            {selectedFilter && (
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Description
                </Typography>

                <Typography>
                  {selectedFilter.description ||
                    "-"}
                </Typography>
              </Box>
            )}

            
          </Stack>
        )}

        {activeTab === 1 && (
          <Stack spacing={3}>
            <TextField
              fullWidth
              required
              sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
              label="Filter Name"
              value={filterName}
              error={!!errors.name}
              helperText={
                errors.name ||
                `${filterName.length}/100`
              }
              inputProps={{ maxLength: 100 }}
              onChange={(e) =>
                setFilterName(
                  e.target.value,
                )
              }
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
              label="Description"
              value={description}
              error={!!errors.description}
              helperText={
                errors.description ||
                `${description.length}/500`
              }
              inputProps={{ maxLength: 500 }}
              onChange={(e) =>
                setDescription(
                  e.target.value,
                )
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isDefault}
                  onChange={(e) =>
                    setIsDefault(
                      e.target.checked,
                    )
                  }
                />
              }
              label="Set as Default Filter"
            />

            {errors.filter && (
              <Typography
                color="error"
                fontSize={14}
              >
                {errors.filter}
              </Typography>
            )}

          
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {activeTab === 0 ? <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            >
              
              <Button
                variant="contained"
                disabled={!selectedFilterId || loading}
                onClick={handleApply}
                 sx={{color:'white',backgroundColor:'black', borderColor:'black'}}
              >
                Apply
              </Button>

              <Button
                variant="outlined"
                disabled={loading}
                sx={{color:'black', borderColor:'black'}}
                onClick={handleStartCreate}
              >
                New
              </Button>

              <Button
                variant="outlined"
                color="error"
                disabled={!selectedFilterId || loading}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>

              <Button
                variant="outlined"
                disabled={!selectedFilterId || loading}
                 sx={{color:'black', borderColor:'black'}}
                onClick={handleStartEdit}
              >
                Edit
              </Button>
            </Stack> :  <Stack
              direction="row"
              spacing={2}
            >
              <Button
                variant="contained"
                onClick={
                  handleSaveOrUpdate
                }
                disabled={saving || loading}
                 sx={{color:'white',backgroundColor:'black', borderColor:'black'}}
              >
                {editingFilter
                  ? "Update Filter"
                  : "Save Filter"}
              </Button>

              <Button
                variant="outlined"
                onClick={resetForm}
                disabled={saving}
                 sx={{color:'black', borderColor:'black'}}
              >
                Clear
              </Button>
            </Stack>}
            <Button
                variant="outlined"
                onClick={onClose}
                sx={{color:'black', borderColor:'black'}}
              >
                Close
              </Button>
        
      </DialogActions>
    </Dialog>
    <ErrorDialog
  open={
    toastOpen &&
    toastSeverity === "error"
  }
  message={toastMessage}
  onClose={() =>
    setToastOpen(false)
  }
/>
<Snackbar
  open={
    toastOpen &&
    toastSeverity !== "error"
  }
  autoHideDuration={3000}
  onClose={() =>
    setToastOpen(false)
  }
  anchorOrigin={{
    vertical: "top",
    horizontal: "center",
  }}
>
  <Alert
    severity={toastSeverity}
    variant="filled"
    onClose={() =>
      setToastOpen(false)
    }
    sx={{ width: "100%" }}
  >
    {toastMessage}
  </Alert>
</Snackbar>
<Dialog
  open={deleteDialogOpen}
  onClose={() =>
    setDeleteDialogOpen(false)
  }
  maxWidth="xs"
  fullWidth
>
  <DialogTitle>
    Delete Filter
  </DialogTitle>

  <DialogContent>
    <Typography>
      Are you sure you want to delete
      this filter?
    </Typography>

    <Typography
      sx={{
        fontWeight: 600,
        mt: 1,
      }}
    >
      {selectedFilter?.name}
    </Typography>
  </DialogContent>

  <DialogActions
    sx={{
      px: 3,
      pb: 2,
      gap: 1,
    }}
  >
    <ButtonComponent
      type="primary"
      label="Cancel"
      onClick={() =>
        setDeleteDialogOpen(false)
      }
      borderRadius="28px"
      width="auto"
      maxWidth="none"
    />

    <ButtonComponent
      type="secondary"
      label="Delete"
      onClick={handleDelete}
      borderRadius="28px"
      width="auto"
      maxWidth="none"
    />
  </DialogActions>
</Dialog>
    </>
  );
};

export default FilterManagerModal;